import type { ReactNode } from "react";
import { Check, ChevronRight, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { wizardLayout } from "./wizardLayout";

export function ProgressiveRowGroup({ children }: { children: ReactNode }) {
  return (
    <div
      className={cn(
        wizardLayout.section,
        "divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface",
      )}
    >
      {children}
    </div>
  );
}

interface ProgressiveRowProps {
  label: string;
  // Collapsed right-hand display of the current selection.
  value?: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // System-determined row: shows a lock, still expandable to view the value.
  locked?: boolean;
  // A value has been committed — show the check rather than the empty marker.
  settled?: boolean;
  // Expanded content (the control, or explanatory copy for locked rows).
  children?: ReactNode;
  className?: string;
}

export function ProgressiveRow({
  label,
  value,
  open,
  onOpenChange,
  locked = false,
  settled = false,
  children,
  className,
}: ProgressiveRowProps) {
  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/40"
      >
        {locked ? (
          <>
            <Lock className="size-4 shrink-0 text-amber-500" />
            <span className="sr-only">Locked</span>
          </>
        ) : settled ? (
          <Check className="size-4 shrink-0 text-primary" strokeWidth={2.75} />
        ) : (
          <span
            aria-hidden
            className="size-4 shrink-0 rounded-full border-2 border-muted-foreground/30"
          />
        )}
        <span className="poppins shrink-0 text-sm font-semibold tracking-tight text-text-primary">
          {label}
        </span>
        <span className="min-w-0 truncate mulish text-sm text-text-muted">
          {value}
        </span>
        <ChevronRight
          className={cn(
            "ml-auto size-4 shrink-0 transition-transform duration-200 ease-out motion-reduce:transition-none",
            open && "rotate-90",
          )}
        />
      </button>
      {children != null && (
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
            open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="overflow-hidden" inert={!open ? true : undefined}>
            <div
              className={cn(
                "px-4 pb-4 pt-1 transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none",
                open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1",
              )}
            >
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
